<template>
  <div class="topup">
    <div class="topup__top">
      <p>
        {{ t('profile.topup.currentBalance') }}
        <span>
          <img src="@icons/currency.svg" alt="currency">
          {{ userBalance }}
        </span>
      </p>
    </div>
    <div class="topup__change">
      <ui-input
        class="topup__change-input"
        type="'text'"
        v-model="amount"
        surfcent
      />
      <svg width="45" viewBox="0 0 45 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M44.0607 12.7689C44.6464 12.1831 44.6464 11.2334 44.0607 10.6476L34.5147 1.10165C33.9289 0.515863 32.9792 0.515863 32.3934 1.10165C31.8076 1.68744 31.8076 2.63718 32.3934 3.22297L40.8787 11.7083L32.3934 20.1935C31.8076 20.7793 31.8076 21.7291 32.3934 22.3149C32.9792 22.9006 33.9289 22.9006 34.5147 22.3149L44.0607 12.7689ZM0 13.2083H43V10.2083H0V13.2083Z" fill="#5B194F"/>
      </svg>
      <ui-input
        type="'text'"
        v-model="amount"
      />
    </div>
    <div class="topup__actions">
      <ui-checkbox
        v-model="isAgree"
      >
        <p>
          {{ t('checkboxes.accept') }}
          <router-link class="topup__actions-link" :to="translations.i18nRoute({ name: 'terms-and-conditions' })">
            {{ t('docs.terms.title') }}
          </router-link>
          <span> & </span>
          <router-link class="topup__actions-link" :to="translations.i18nRoute({ name: 'privacy-policy' })">
            {{ t('docs.policy.title') }}
          </router-link>
        </p>
      </ui-checkbox>
      <i18n-t
          class="topup__actions-text"
          scope="global"
          tag="p"
          :keypath="`profile.topup.text`"
      >
        <template #paymentMin>
          <span>{{ paymentMin }}</span>
        </template>
        <template #paymentMax>
          <span>{{ paymentMax }}</span>
        </template>
      </i18n-t>
    </div>
    <ui-button
        class="topup__button"
        :isDisabled="!isFormValid"
        :isLoading="false"
        @click="topUp"
    >
      {{ t('buttons.topup') }}
    </ui-button>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {useAuthStore} from "@/stores/auth.js";
import {computed, onMounted, ref} from "vue";
import UiInput from "@/components/ui/ui-input.vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import translations from "@/core/helpers/translations.js";
import UiButton from "@/components/ui/ui-button.vue";
import {Processes} from "@/core/api/processes.js";
import {ElNotification} from "element-plus";

const {t} = useI18n()
const authStore = useAuthStore()

const paymentMin = ref('')
const paymentMax = ref('')
const userBalance = computed(() => {
  return authStore.user ? authStore.user.balance.amount : 0
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})

const amount = ref(5)
const isAgree = ref(false)

const isFormValid = computed(() => {
  return (
    amount.value >= paymentMin.value &&
    amount.value <= paymentMax.value &&
    isAgree.value
  )
})

const topUp = async () => {
  const res = await Processes.topUpBalance(amount.value, accessToken.value)

  if (res.data.deposit.transaction.process) {
    if (res.data.deposit.transaction.process.url) {
      window.location.href = res.data.deposit.transaction.process.url
    } else {
      console.log(res.data)
    }
  } else {
    ElNotification({
      dangerouslyUseHTMLString: true,
      message: res.errors[0].message,
      customClass: 'notification'
    })
  }
}

onMounted( async () => {
  const res = await Processes.getPaymentLimits()
  paymentMin.value = res.data.paymentsLimits.minAmount
  paymentMax.value = res.data.paymentsLimits.maxAmount
})
</script>

<style lang="scss" scoped>
.topup {
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 37px;
  position: relative;
  z-index: 1;

  &__top {
    width: 100%;
    padding-bottom: 20px;
    border-bottom: 2px solid #5B194F;

    & p {
      display: flex;
      align-items: center;
      gap: 20px;

      color: #5B194F;
      font-size: 20px;
      font-weight: 600;

      & span {
        display: flex;
        align-items: center;
        gap: 5px;

        color: #2B3044;
        font-size: 18px;
        font-weight: 500;

        & img {
          width: 29px;
        }
      }
    }
  }

  &__change {
    display: flex;
    align-items: center;
    gap: 25px;

    & svg {
      flex-shrink: 0;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 9px;

    &-link {
      text-decoration: underline;
      margin-inline: 5px;
      transition: 0.2s;
      color: #5b194f;

      &:hover {
        color: #722363;
      }
    }

    &-text {
      width: 690px;

      color: #5B194F;
      font-size: 13px;
      font-weight: 500;
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 50px;
  }
}

:deep(.topup__change-input .block__input) {
  padding-left: 40px;
}

@media (max-width: 1000px) {
  .topup {
    &__actions {
      &-text {
        width: 100%;
      }
    }
  }
}

@media (max-width: 450px) {
  .topup {
    &__change {
      flex-direction: column;

      & svg {
        transform: rotate(90deg);
      }
    }
  }
}
</style>